import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  MoreHorizontal,
  FileText,
  Loader2,
  Edit,
  Copy,
  Trash2,
  Eye,
  Mail,
  Pencil,
  WifiOff,
  CheckCircle2,
  BookmarkPlus,
} from "lucide-react";
import { DashboardItinerary } from "@/lib/models/dashboard";
import { ItineraryStatus } from "../builder/models/itinerary.model";
import { useI18n } from '@/hooks/useI18n';
import { useModal } from "@/contexts/ModalContext";
import { toast } from "@/hooks/useToast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { OfflineItinerariesService } from "@/services/offline-itineraries.service";

interface ItineraryActionsProps {
  itinerary: DashboardItinerary;
  onEdit?: (itineraryId: string) => void;
  onDelete?: (itineraryId: string) => void;
  onDuplicate?: (itineraryId: string) => void;
  onViewTravelRequest?: (travelRequestId: string) => void;
  onViewItinerary?: (itineraryId: string) => void;
  onUpdateStatus?: (itineraryId: string, status: ItineraryStatus) => void;
  /** Chiamata alla chiusura della modal di condivisione dopo un invio riuscito:
   *  lo status dell'itinerario è cambiato, la lista in memoria va rifetchata. */
  onShared?: () => void;
  isAdmin?: boolean;
  isTraveler?: boolean;
  userRole?: string;
  variant?: "card" | "table";
  onOpenChange?: (open: boolean) => void;
  onPdfStateChange?: (isGenerating: boolean, isSuccess: boolean) => void;
}

export const ItineraryActions = ({
  itinerary,
  onEdit,
  onDelete,
  onDuplicate,
  onViewTravelRequest,
  onViewItinerary,
  onUpdateStatus,
  onShared,
  isAdmin,
  isTraveler,
  userRole,
  variant = "card",
  onOpenChange,
  onPdfStateChange,
}: ItineraryActionsProps) => {
  const { t } = useI18n();
  const { openModal } = useModal();
  const { userData, logoUrl, logoSmallUrl } = useAuth();
  const [isGeneratingPDFBE, setIsGeneratingPDFBE] = useState(false);
  const [isDownloadingOffline, setIsDownloadingOffline] = useState(false);
  const [isAlreadyDownloaded, setIsAlreadyDownloaded] = useState(false);

  useEffect(() => {
    OfflineItinerariesService.isDownloaded(itinerary.id).then(setIsAlreadyDownloaded);
  }, [itinerary.id]);
  const handleDownloadOffline = async () => {
    if (isDownloadingOffline) return;
    setIsDownloadingOffline(true);
    try {
      // Le attività passano dalla RPC: il pacchetto offline finisce su IndexedDB del
      // dispositivo del cliente, quindi i costi per travel object non devono nemmeno
      // arrivare al browser. La RPC li azzera server-side per il traveler.
      const [{ data: itineraryRecord }, { data: activities }] = await Promise.all([
        supabase.from('itineraries').select('*').eq('id', itinerary.id).single(),
        supabase.rpc('get_itinerary_activities' as any, { p_itinerary_id: itinerary.id }),
      ]);

      if (!itineraryRecord) throw new Error('Itinerary not found');

      const dayMap = new Map<string, any[]>();
      for (const act of activities ?? []) {
        if (!dayMap.has(act.date)) dayMap.set(act.date, []);
        dayMap.get(act.date)!.push(act);
      }
      const itinerary_days = Array.from(dayMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, acts]) => ({ date, activities: acts }));

      const fullItinerary = { ...itineraryRecord, itinerary_days };

      const sharedBy = {
        first_name: userData?.first_name ?? '',
        last_name: userData?.last_name ?? '',
        logo_url: logoUrl ?? undefined,
        agency_name: userData?.agency_name ?? undefined,
      };

      await OfflineItinerariesService.downloadItinerary(
        fullItinerary,
        sharedBy,
      );
      setIsAlreadyDownloaded(true);

      toast({ variant: 'success',
        title: t('offline.downloadSuccess'),
        description: t('offline.downloadSuccessDesc'),
      });
    } catch (err) {
      console.error('Error downloading offline:', err);
      toast({
        title: t('errors.generic'),
        description: t('offline.downloadError'),
        variant: 'destructive',
      });
    } finally {
      setIsDownloadingOffline(false);
    }
  };

  const handleViewItinerary = () => {
    if (!onViewItinerary) return;
    onViewItinerary(itinerary.id);
  };

  const handleViewTravelRequest = () => {
    if (!onViewTravelRequest) return;
    onViewTravelRequest(itinerary.travelRequest.id);
  };

  const handleEdit = () => {
    if (!onEdit) return;
    onEdit(itinerary.id);
  };

  const handleDuplicate = () => {
    if (!onDuplicate) return;
    onDuplicate(itinerary.id);
  };

  const handleDelete = () => {
    if (!onDelete) return;
    onDelete(itinerary.id);
  };

  const handleSaveAsTemplateModal = () => {
    openModal("save-as-template", { itinerary });
  };

  const handleSendQuoteModal = () => {
    openModal("send-quote", {
      itinerary,
      onSuccessClose: onShared,
    });
  };

  const handleGeneratePDFBE = async () => {
    if (isGeneratingPDFBE) return;
    setIsGeneratingPDFBE(true);
    onPdfStateChange?.(true, false);
    try {
      // Logo già risolto da AuthContext secondo la preferenza dell'utente
      const resolvedLogoUrl = logoUrl;
      const resolvedLogoSmallUrl = logoSmallUrl;
      const { data, error } = await supabase.functions.invoke("generate-pdf", {
        body: JSON.stringify({
          itineraryId: itinerary.id,
          ...(resolvedLogoUrl ? { logoUrl: resolvedLogoUrl } : {}),
          ...(resolvedLogoSmallUrl ? { logoSmallUrl: resolvedLogoSmallUrl } : {}),
        }),
      });
      if (error || !data?.pdfBase64)
        throw new Error(error?.message || "No PDF returned");
      const byteChars = atob(data.pdfBase64);
      const byteArr = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++)
        byteArr[i] = byteChars.charCodeAt(i);
      const blob = new Blob([byteArr], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.fileName || `itinerary-${itinerary.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      
      // Show success state
      onPdfStateChange?.(false, true);
      setTimeout(() => {
        onPdfStateChange?.(false, false);
      }, 3500);
      
      toast({ variant: 'success',
        title: t("itinerary.pdfGenerated"),
        description: t("itinerary.pdfGeneratedDesc"),
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      onPdfStateChange?.(false, false);
      toast({
        title: t("errors.generic"),
        description: t("itinerary.pdfGenerationError"),
        variant: "destructive",
      });
    } finally {
      setIsGeneratingPDFBE(false);
    }
  };

  // Stessi ruoli che possono aprire il Playground (e che la RPC accetta: per gli
  // altri solleva eccezione). I traveler non vedono la voce in nessun caso.
  const canSaveAsTemplate =
    !isTraveler &&
    (isAdmin ||
      ["admin", "travel_designer", "travel_advisor_member", "agency"].includes(
        userRole ?? ""
      ));

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild onClick={stopPropagation}>
        {variant === "card" ? (
          <Button
            variant="ghost"
            size="sm"
            className="w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white/95"
            title="Menu"
          >
            <MoreHorizontal className="h-5 w-5 text-gray-800" />
          </Button>
        ) : (
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {isTraveler || userRole === "agency" ? (
          <>
            {/* <DropdownMenuItem onClick={(e) => handleClick(e, handleViewItinerary)}>
              <Eye className="h-4 w-4 mr-2" />
              {t("itinerary.viewItinerary")}
            </DropdownMenuItem> */}
            {userRole === "agency" && (
              <DropdownMenuItem onClick={(e) => handleClick(e, handleViewTravelRequest)}>
                <Eye className="h-4 w-4 mr-2" />
                {t("travelRequest.viewTripRequest")}
              </DropdownMenuItem>
            )}
          </>
        ) : (
          <>
            <DropdownMenuItem onClick={(e) => handleClick(e, handleViewItinerary)}>
              <Pencil className="h-4 w-4 mr-2" />
              {t("itinerary.editItinerary")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => handleClick(e, handleViewTravelRequest)}>
              <Eye className="h-4 w-4 mr-2" />
              {t("travelRequest.viewTripRequest")}
            </DropdownMenuItem>
            {isAdmin && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(
                    `https://www.backoffice.place2be.it/itineraries/edit/${itinerary.id}`,
                    "_blank"
                  );
                }}
              >
                <Edit className="h-4 w-4 mr-2" />
                {t("itinerary.openInBackoffice")}
              </DropdownMenuItem>
            )}
          </>
        )}

        {!isTraveler && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={(e) => handleClick(e, handleSendQuoteModal)}>
              <Mail className="h-4 w-4 mr-2" />
              {t("itinerary.sendToClient")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        <DropdownMenuItem
          onClick={(e) => handleClick(e, handleGeneratePDFBE)}
          disabled={isGeneratingPDFBE}
        >
          {isGeneratingPDFBE ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <FileText className="h-4 w-4 mr-2" />
          )}
          {isGeneratingPDFBE
            ? t("itinerary.generatingPDF")
            : t("itinerary.generatePDF")}
        </DropdownMenuItem>

        {(window.matchMedia('(display-mode: standalone)').matches ||
          window.location.hostname === 'localhost') && (
          <DropdownMenuItem
            onClick={(e) => handleClick(e, handleDownloadOffline)}
            disabled={isDownloadingOffline}
          >
            {isDownloadingOffline ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : isAlreadyDownloaded ? (
              <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
            ) : (
              <WifiOff className="h-4 w-4 mr-2" />
            )}
            {isDownloadingOffline
              ? t("offline.downloading")
              : isAlreadyDownloaded
              ? t("offline.downloadedAgain")
              : t("offline.downloadOffline")}
          </DropdownMenuItem>
        )}

        {/* Fuori dal blocco che nasconde duplica/elimina sugli itinerari inviati o
            conclusi: un template si ricava da un viaggio in qualsiasi stato. */}
        {canSaveAsTemplate && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={(e) => handleClick(e, handleSaveAsTemplateModal)}>
              <BookmarkPlus className="h-4 w-4 mr-2" />
              {t("itinerary.saveAsTemplate")}
            </DropdownMenuItem>
          </>
        )}

        {!isTraveler &&
          userRole !== "agency" &&
          itinerary.status !== "sended" &&
          itinerary.status !== "completed" && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={(e) => handleClick(e, handleDuplicate)}>
                <Copy className="h-4 w-4 mr-2" />
                {t("common.duplicate")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => handleClick(e, handleDelete)}>
                <Trash2 className="h-4 w-4 mr-2" />
                {t("common.delete")}
              </DropdownMenuItem>
            </>
          )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
